import React, { useEffect, useMemo, useState } from 'react'
import { interceptExternalLinkClick } from '../lib/nativeShell'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  FlakyTest,
  TestDispatchStatus,
  TestHistoryEntry,
  TestResult,
  TestRunDetail,
  TestSuiteRun,
  dispatchTestTrackerRun,
  getTestTrackerDispatchStatus,
  getTestTrackerFlaky,
  getTestTrackerHistory,
  getTestTrackerRun,
  getTestTrackerRuns,
  getTestTrackerSuites,
} from '../api/client'

// Animation vocabulary (CLAUDE.md): surfaces pop with the 500/30 spring,
// icons/buttons use the 400/20 one.
const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const ICON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }
const SURFACE_POP = {
  initial: { opacity: 0, scale: 0.95, y: -8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: -8 },
  transition: SURFACE_SPRING,
  style: { transformOrigin: 'top center' },
}

const FLAKY_WINDOWS = [10, 20, 50]

// Suite colors are assigned by position, not by name — the suite vocabulary
// comes from the server and can grow.
const SUITE_COLORS = ['#3b82f6', '#8b5cf6', '#14b8a6', '#f59e0b', '#ec4899']

const PASS_COLOR = '#22c55e'
const FAIL_COLOR = '#ef4444'
const SKIP_COLOR = '#d1d5db'
const DURATION_COLOR = '#6366f1'

// Test harnesses colorize their own output; the raw SGR codes are stored.
const ANSI = new RegExp(String.fromCharCode(27) + '\\[[0-9;]*m', 'g')
const stripAnsi = (s: string) => s.replace(ANSI, '')

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  const s = ms / 1000
  if (s < 90) return `${s.toFixed(1)} s`
  const m = Math.floor(s / 60)
  return `${m}m ${Math.round(s % 60)}s`
}

// `started_at` is DATETIME(3) holding UTC, so the API hands back a naive ISO
// string. `new Date()` would read that as browser-local and shift every EC2 run
// by the viewer's offset — pin it to UTC, then render in the viewer's zone.
const asUtcDate = (iso: string): Date =>
  new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`)

const formatTime = (iso: string | null): string => {
  if (!iso) return '—'
  const d = asUtcDate(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const shortSha = (sha: string | null) => (sha ? sha.slice(0, 7) : '—')

// Coarse on purpose: the only question this answers is "has today's live run
// happened yet", so anything finer than an hour is noise.
const formatAgo = (iso: string | null): string => {
  if (!iso) return 'never'
  const hours = (Date.now() - asUtcDate(iso).getTime()) / 3_600_000
  if (Number.isNaN(hours)) return 'never'
  if (hours < 1) return 'under an hour ago'
  if (hours < 48) return `${Math.round(hours)}h ago`
  return `${Math.round(hours / 24)}d ago`
}

// The app's HTTPException handler renames `detail` to `error` (main.py), but
// FastAPI's own validation errors still come back under `detail`.
const apiMessage = (e: any, fallback: string): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback

// Playwright ids carry the browser project — `spec.ts::title [webkit]` — so one
// spec yields up to three near-identical rows. Split it out into its own badge.
const splitBrowser = (label: string): { title: string; browser: string | null } => {
  const match = label.match(/^(.*)\s\[([^\]]+)\]$/)
  return match ? { title: match[1], browser: match[2] } : { title: label, browser: null }
}

// Pass rate is meaningless for an `error` run — the harness produced no report,
// so its zero counts aren't a green run.
const passRate = (run: TestSuiteRun): number | null => {
  if (run.status === 'error') return null
  const graded = run.passed + run.failed
  return graded > 0 ? (run.passed / graded) * 100 : null
}

// One scale per suite: backend runs ~22 min, frontend ~9 s, so a shared
// duration axis would flatten everything but the backend.
const durationScale = (runs: TestSuiteRun[]) => {
  const max = Math.max(0, ...runs.map((r) => r.duration_ms ?? 0))
  if (max >= 90_000) return { unit: 'min', divisor: 60_000, digits: 1 }
  if (max >= 1000) return { unit: 's', divisor: 1000, digits: 1 }
  return { unit: 'ms', divisor: 1, digits: 0 }
}

const RUN_STATUS_STYLE: Record<string, { label: string; className: string }> = {
  passed: { label: 'passed', className: 'bg-green-100 text-green-800' },
  failed: { label: 'failed', className: 'bg-rose-100 text-rose-800' },
  // A crashed harness is not a red run: no report was produced at all.
  error: { label: 'harness error', className: 'bg-amber-100 text-amber-800' },
}

const TEST_STATUS_STYLE: Record<string, string> = {
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-rose-100 text-rose-800',
  error: 'bg-amber-100 text-amber-800',
  skipped: 'bg-gray-100 text-gray-600',
}

const HISTORY_DOT: Record<string, string> = {
  passed: 'bg-green-500',
  failed: 'bg-danger',
  error: 'bg-amber-500',
  skipped: 'bg-gray-300',
}

const StatusBadge: React.FC<{ status: string; title?: string }> = ({ status, title }) => {
  const style = RUN_STATUS_STYLE[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-700',
  }
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  )
}

const BrowserBadge: React.FC<{ browser: string | null }> = ({ browser }) =>
  browser ? (
    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-900 whitespace-nowrap">
      {browser}
    </span>
  ) : null

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <motion.span
    animate={{ rotate: open ? 90 : 0 }}
    transition={ICON_SPRING}
    className="inline-block text-gray-400 select-none"
  >
    ▸
  </motion.span>
)

const FailureMessage: React.FC<{ message: string }> = ({ message }) => (
  <pre className="mt-2 text-xs whitespace-pre-wrap break-words rounded border border-rose-200 bg-rose-50 text-rose-800 p-3 max-h-64 overflow-y-auto">
    {stripAnsi(message)}
  </pre>
)

// ------------------------------------------------------------------- charts

// Every chart on this page plots one point per run, so the category value has
// to be unique per run — the run id, which is also what the tooltip needs to
// name a point. It is not what the axis should *read* as, though: a row of run
// ids answers no question anybody brings to a trend.
const chartLabel = (run: TestSuiteRun) => `#${run.id}`

// What a point says when you hover it: when it ran, then which run it was.
const chartTooltip = (run: TestSuiteRun) => `${formatTime(run.started_at)} · #${run.id}`

/**
 * The tick text for each run, in run order: a date on the first run of each
 * day and an empty string on every run after it that day.
 *
 * The points stay one-per-run — two pushes an hour apart are two points, and
 * collapsing them would hide exactly the within-day regression this page is
 * read for. Only the *labels* thin out, which is what turns a row of ids into
 * something that scans as a calendar. A push writes three to five suite rows
 * at once, so without the thinning a single day can carry a dozen identical
 * date ticks.
 *
 * A run with no `started_at` gets no tick and does not open a day: the next
 * dated run that day still carries the date.
 *
 * `maxLabels` thins further, evenly, for a chart too narrow to carry one label
 * per day — a 342px suite card fits about four before "Sep 16Sep 17Sep 18" runs
 * together into one word. One a day is the ceiling, never the guarantee.
 */
export const dayTicks = (
  runs: { started_at: string | null }[],
  maxLabels = Number.POSITIVE_INFINITY,
): string[] => {
  let openDay = ''
  const ticks = runs.map((r) => {
    if (!r.started_at) return ''
    const d = asUtcDate(r.started_at)
    if (Number.isNaN(d.getTime())) return ''
    // Keyed on the date in the *viewer's* zone, the same zone the tick renders
    // in — keying on the UTC date would start a new day mid-afternoon for
    // anyone west of Greenwich.
    const day = d.toDateString()
    if (day === openDay) return ''
    openDay = day
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  })

  const labelled = ticks.reduce<number[]>(
    (acc, tick, i) => (tick ? [...acc, i] : acc),
    [],
  )
  if (labelled.length <= maxLabels) return ticks
  // Evenly spaced rather than "the first N": a chart that labelled January and
  // then nothing would be worse than one that labels every third day.
  const step = Math.ceil(labelled.length / maxLabels)
  labelled.forEach((i, n) => {
    if (n % step !== 0) ticks[i] = ''
  })
  return ticks
}

// How many dates each chart's width carries. The trend and full-width duration
// charts run the page width; the sparkline in a suite card is a third of it.
const WIDE_CHART_LABELS = 14
const COMPACT_CHART_LABELS = 4

/**
 * XAxis props shared by all three charts, so they can't drift apart.
 *
 * `interval={0}` is load-bearing: it stops recharts thinning the ticks itself,
 * which is what keeps the tick index in step with the data index that
 * `dayTicks` is indexed by. Rendering every tick is free here because all but
 * one a day are empty.
 */
const runAxisProps = (runs: TestSuiteRun[], fontSize: number, maxLabels: number) => {
  const ticks = dayTicks(runs, maxLabels)
  return {
    dataKey: 'label',
    tick: { fontSize },
    tickLine: false,
    interval: 0 as const,
    tickFormatter: (_value: string, index: number) => ticks[index] ?? '',
  }
}

// The axis no longer names the point, so the tooltip has to.
const runTooltipLabel = (label: unknown, payload: readonly any[] | undefined) =>
  payload?.[0]?.payload?.tooltip ?? String(label ?? '')

const PassFailBars: React.FC<{ runs: TestSuiteRun[] }> = ({ runs }) => {
  const data = runs.map((r) => ({
    label: chartLabel(r),
    tooltip: chartTooltip(r),
    passed: r.passed,
    failed: r.failed,
    skipped: r.skipped,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis {...runAxisProps(runs, 11, WIDE_CHART_LABELS)} />
        <YAxis tick={{ fontSize: 11 }} tickLine={false} />
        <Tooltip labelFormatter={runTooltipLabel} />
        <Legend />
        <Bar dataKey="passed" name="Passed" stackId="1" fill={PASS_COLOR} />
        <Bar dataKey="failed" name="Failed" stackId="1" fill={FAIL_COLOR} />
        <Bar dataKey="skipped" name="Skipped" stackId="1" fill={SKIP_COLOR} />
      </BarChart>
    </ResponsiveContainer>
  )
}

// Cross-suite view: pass rate is the only metric comparable between a 640-test
// backend run and a 39-test e2e run. Runs share an X axis in chronological
// order; each run belongs to one suite, so series are sparse and connected.
const PassRateLines: React.FC<{ runs: TestSuiteRun[]; suites: string[] }> = ({
  runs,
  suites,
}) => {
  const data = runs.map((r) => ({
    label: chartLabel(r),
    tooltip: chartTooltip(r),
    [r.suite]: passRate(r),
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis {...runAxisProps(runs, 11, WIDE_CHART_LABELS)} />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11 }}
          tickLine={false}
          tickFormatter={(v: number) => `${v}%`}
        />
        <Tooltip
          formatter={(v: number) => `${v.toFixed(1)}%`}
          labelFormatter={runTooltipLabel}
        />
        <Legend />
        {suites.map((suite, i) => (
          <Line
            key={suite}
            type="monotone"
            dataKey={suite}
            name={suite}
            stroke={SUITE_COLORS[i % SUITE_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

const DurationChart: React.FC<{ runs: TestSuiteRun[]; compact?: boolean }> = ({
  runs,
  compact,
}) => {
  const scale = durationScale(runs)
  const data = runs.map((r) => ({
    label: chartLabel(r),
    tooltip: chartTooltip(r),
    duration: r.duration_ms == null ? null : r.duration_ms / scale.divisor,
  }))
  const fmt = (v: number) => `${v.toFixed(scale.digits)} ${scale.unit}`
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          {...runAxisProps(
            runs,
            10,
            compact ? COMPACT_CHART_LABELS : WIDE_CHART_LABELS,
          )}
        />
        <YAxis
          tick={{ fontSize: 10 }}
          tickLine={false}
          width={compact ? 38 : 60}
          tickFormatter={(v: number) => v.toFixed(scale.digits)}
        />
        <Tooltip formatter={(v: number) => fmt(v)} labelFormatter={runTooltipLabel} />
        <Area
          type="monotone"
          dataKey="duration"
          name={`Duration (${scale.unit})`}
          stroke={DURATION_COLOR}
          fill="#c7d2fe"
          connectNulls
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// -------------------------------------------------------------- suite cards

const SuiteCard: React.FC<{
  suite: string
  run: TestSuiteRun | null
  selected: boolean
  onSelect: () => void
}> = ({ suite, run, selected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`text-left rounded-lg border p-4 transition-colors ${
      selected
        ? 'border-amber-500 bg-amber-50'
        : 'border-gray-200 bg-white hover:bg-gray-50'
    }`}
  >
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="font-mono text-sm font-semibold text-gray-800">{suite}</span>
      {run ? (
        <StatusBadge status={run.status} title={run.error_message ?? undefined} />
      ) : (
        <span className="text-xs text-gray-400">no runs</span>
      )}
    </div>
    {run ? (
      <>
        <div className="flex items-baseline gap-3 text-sm tabular-nums">
          <span className="text-green-700 font-semibold">{run.passed}</span>
          <span className={run.failed > 0 ? 'text-rose-700 font-semibold' : 'text-gray-400'}>
            {run.failed} failed
          </span>
          <span className="text-gray-400">{run.skipped} skipped</span>
        </div>
        <div className="mt-1 text-xs text-gray-500">
          {formatDuration(run.duration_ms)} · {formatTime(run.started_at)}
        </div>
        <div className="mt-0.5 text-xs text-gray-400 font-mono">
          {shortSha(run.git_sha)} · {run.env ?? '—'}/{run.clone ?? '—'}
        </div>
      </>
    ) : (
      <div className="text-xs text-gray-400">Run the tracker to populate this suite.</div>
    )}
  </button>
)

// ------------------------------------------------------------- flaky section

const FlakyRow: React.FC<{ flaky: FlakyTest }> = ({ flaky }) => {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<TestHistoryEntry[] | null>(null)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const { title, browser } = splitBrowser(flaky.name ?? flaky.test_id)

  useEffect(() => {
    if (!open || history) return
    getTestTrackerHistory(flaky.suite, flaky.test_id)
      .then((res) => setHistory(res.history))
      .catch((e) => setHistoryError(e?.response?.data?.detail || e.message || 'Failed to load'))
  }, [open])

  return (
    <>
      <tr
        onClick={() => setOpen(!open)}
        className={`border-t border-gray-100 cursor-pointer ${open ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
      >
        <td className="px-2 py-2 text-center">
          <Chevron open={open} />
        </td>
        <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">
          {flaky.suite}
        </td>
        <td className="px-3 py-2 text-gray-800">
          <span className="break-all">{title}</span>
          <BrowserBadge browser={browser} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{flaky.runs}</td>
        <td className="px-3 py-2 text-right tabular-nums text-green-700">{flaky.passed}</td>
        <td className="px-3 py-2 text-right tabular-nums text-rose-700">{flaky.failed}</td>
        <td className="px-3 py-2 text-right tabular-nums text-gray-600">
          {flaky.retry_flakes || '—'}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium">
          {(flaky.fail_rate * 100).toFixed(0)}%
        </td>
        <td className="px-3 py-2">
          <div className="flex gap-1">
            {flaky.reasons.map((r) => (
              <span
                key={r}
                title={
                  r === 'mixed'
                    ? 'Both passed and failed across the window'
                    : 'Playwright retried it and it then passed'
                }
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  r === 'mixed' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                }`}
              >
                {r}
              </span>
            ))}
          </div>
        </td>
      </tr>
      {/* A `<tr>` can't be a motion child inside AnimatePresence without
          breaking table layout, so the expanded row pops in and cuts out. */}
      {open && (
        <tr className="border-t border-gray-100 bg-gray-50">
          <td></td>
          <td colSpan={8} className="px-3 py-3">
            <motion.div {...SURFACE_POP}>
                <div className="text-xs font-mono text-gray-500 break-all mb-2">
                  {flaky.test_id}
                </div>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                  History (oldest → newest)
                </div>
                {historyError ? (
                  <div className="text-xs text-rose-700">{historyError}</div>
                ) : history == null ? (
                  <div className="text-xs text-gray-400">Loading…</div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {[...history].reverse().map((h) => (
                      <span
                        key={h.run_id}
                        title={`run #${h.run_id} · ${h.status} · ${formatDuration(
                          h.duration_ms,
                        )} · ${formatTime(h.started_at)} · ${shortSha(h.git_sha)}`}
                        className={`h-4 w-4 rounded-sm ${HISTORY_DOT[h.status] ?? 'bg-gray-200'}`}
                      />
                    ))}
                  </div>
                )}
                {flaky.sample_failure_message && (
                  <FailureMessage message={flaky.sample_failure_message} />
                )}
            </motion.div>
          </td>
        </tr>
      )}
    </>
  )
}

// ------------------------------------------------------------ run drill-down

// Failures first: a backend run carries ~640 rows, and the reason to open one
// is almost always the red ones.
const DETAIL_FILTERS: { key: string; label: string; statuses: string[] }[] = [
  { key: 'failures', label: 'Failures', statuses: ['failed', 'error'] },
  { key: 'skipped', label: 'Skipped', statuses: ['skipped'] },
  { key: 'all', label: 'All tests', statuses: [] },
]

// `inTree` drops what the surrounding tree already shows: the describe chain
// (the row's ancestors) and the classname path line.
const TestRow: React.FC<{ test: TestResult; inTree?: boolean }> = ({ test, inTree }) => {
  const [open, setOpen] = useState(false)
  const full = splitBrowser(test.name ?? test.test_id)
  const title = inTree ? full.title.split(' > ').slice(-1)[0] : full.title
  const browser = full.browser
  const hasMessage = !!test.failure_message
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => hasMessage && setOpen(!open)}
        className={`w-full flex items-start gap-2 px-3 py-1.5 text-left text-xs ${
          hasMessage ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <span className="w-3 shrink-0 pt-0.5">{hasMessage && <Chevron open={open} />}</span>
        <span
          className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
            TEST_STATUS_STYLE[test.status] ?? 'bg-gray-100 text-gray-700'
          }`}
        >
          {test.status}
        </span>
        <span className="flex-1 min-w-0 text-gray-800 break-all">
          {title}
          <BrowserBadge browser={browser} />
          {!inTree && (
            <span className="block text-[10px] text-gray-400 font-mono break-all">
              {test.classname ?? ''}
            </span>
          )}
        </span>
        <span className="shrink-0 tabular-nums text-gray-500">
          {formatDuration(test.duration_ms)}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && test.failure_message && (
          <motion.div {...SURFACE_POP} className="px-3 pb-3">
            <FailureMessage message={test.failure_message} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ------------------------------------------------------------ test tree view

// Grouping path for a test, derived from its id: pytest classnames are dotted
// module paths (`tests.server.bulk_edit.test_compiler`), Playwright/vitest
// classnames are file paths whose names carry the describe chain
// (`e2e/tests/smoke.spec.ts` + `smoke > title [webkit]`). The last describe
// part is the leaf title TestRow renders, so it stays out of the path. Each
// segment remembers the separator that joined it to its parent, so collapsed
// single-child chains re-render as the original spelling.
type TreeSegment = { label: string; sep: string }

const testSegments = (test: TestResult): TreeSegment[] => {
  const segments: TreeSegment[] = []
  const cls = test.classname ?? ''
  if (cls.includes('/')) {
    cls
      .split('/')
      .filter(Boolean)
      .forEach((part, i) => segments.push({ label: part, sep: i === 0 ? '' : '/' }))
  } else if (cls) {
    cls.split('.').forEach((part, i) => segments.push({ label: part, sep: i === 0 ? '' : '.' }))
  }
  const { title } = splitBrowser(test.name ?? test.test_id)
  title
    .split(' > ')
    .slice(0, -1)
    .forEach((part) => segments.push({ label: part, sep: ' › ' }))
  return segments
}

type TestTreeNode = {
  key: string
  label: string
  children: TestTreeNode[]
  tests: TestResult[]
  total: number
  failed: number
  durationMs: number
}

// The root's label is the prefix every test shares (`tests.server`,
// `e2e/tests`); it renders as a caption, and its children are the top level.
const buildTestTree = (tests: TestResult[]): TestTreeNode => {
  type Draft = {
    label: string
    sep: string
    children: Map<string, Draft>
    tests: TestResult[]
  }
  const root: Draft = { label: '', sep: '', children: new Map(), tests: [] }
  for (const test of tests) {
    let node = root
    for (const seg of testSegments(test)) {
      let child = node.children.get(seg.label)
      if (!child) {
        child = { label: seg.label, sep: seg.sep, children: new Map(), tests: [] }
        node.children.set(seg.label, child)
      }
      node = child
    }
    node.tests.push(test)
  }

  const finalize = (draft: Draft, parentKey: string): TestTreeNode => {
    // Collapse single-child chains — `tests` → `server`, or a spec file whose
    // only child is its sole top-level describe — into one node.
    let d = draft
    let label = d.label
    while (d.tests.length === 0 && d.children.size === 1) {
      const only = [...d.children.values()][0]
      label = label ? label + only.sep + only.label : only.label
      d = only
    }
    const key = `${parentKey}/${label}`
    const children = [...d.children.values()]
      .map((c) => finalize(c, key))
      .sort((a, b) => a.label.localeCompare(b.label))
    const total = d.tests.length + children.reduce((n, c) => n + c.total, 0)
    const failed =
      d.tests.filter((t) => t.status === 'failed' || t.status === 'error').length +
      children.reduce((n, c) => n + c.failed, 0)
    const durationMs =
      d.tests.reduce((n, t) => n + (t.duration_ms ?? 0), 0) +
      children.reduce((n, c) => n + c.durationMs, 0)
    return { key, label, children, tests: d.tests, total, failed, durationMs }
  }

  return finalize(root, '')
}

const TestTreeView: React.FC<{ tests: TestResult[]; filtered: boolean }> = ({
  tests,
  filtered,
}) => {
  const tree = useMemo(() => buildTestTree(tests), [tests])
  const [toggled, setToggled] = useState<Map<string, boolean>>(new Map())

  // A filtered tree is already narrow, so open it; otherwise open only the
  // branches that hold failures — the categories themselves are the overview.
  const isOpen = (node: TestTreeNode) =>
    toggled.get(node.key) ?? (filtered || node.failed > 0)

  const setAll = (open: boolean) => {
    const next = new Map<string, boolean>()
    const walk = (n: TestTreeNode) => {
      next.set(n.key, open)
      n.children.forEach(walk)
    }
    tree.children.forEach(walk)
    setToggled(next)
  }

  const renderNode = (node: TestTreeNode, depth: number): React.ReactNode => {
    const open = isOpen(node)
    return (
      <div key={node.key}>
        <button
          type="button"
          onClick={() => setToggled(new Map(toggled).set(node.key, !open))}
          className="w-full flex items-center gap-2 py-1.5 pr-3 text-left text-xs hover:bg-gray-50 border-b border-gray-100"
          style={{ paddingLeft: 12 + depth * 16 }}
        >
          <Chevron open={open} />
          <span className="font-mono text-gray-800 break-all">{node.label}</span>
          <span className="tabular-nums text-gray-400">{node.total}</span>
          {node.failed > 0 && (
            <span className="tabular-nums text-rose-700 font-medium">{node.failed} failed</span>
          )}
          <span className="ml-auto shrink-0 tabular-nums text-gray-400">
            {formatDuration(node.durationMs)}
          </span>
        </button>
        {open && (
          <motion.div {...SURFACE_POP}>
            {node.children.map((c) => renderNode(c, depth + 1))}
            {node.tests.map((t) => (
              <div key={t.id} style={{ paddingLeft: 12 + depth * 16 }}>
                <TestRow test={t} inTree />
              </div>
            ))}
          </motion.div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 bg-gray-50 text-[10px] text-gray-500">
        <span className="font-mono">{tree.label || '(all tests)'}</span>
        <span className="ml-auto flex gap-2">
          <button type="button" onClick={() => setAll(true)} className="hover:text-gray-700">
            expand all
          </button>
          <button type="button" onClick={() => setAll(false)} className="hover:text-gray-700">
            collapse all
          </button>
        </span>
      </div>
      {tree.children.map((c) => renderNode(c, 0))}
      {/* A run whose tests all share one grouping path yields a childless
          root; fall back to its direct rows. */}
      {tree.children.length === 0 &&
        tree.tests.map((t) => <TestRow key={t.id} test={t} inTree />)}
    </div>
  )
}

const RunDetail: React.FC<{ run: TestSuiteRun }> = ({ run }) => {
  const [filter, setFilter] = useState('failures')
  const [detail, setDetail] = useState<TestRunDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [view, setView] = useState<'tree' | 'list'>('tree')

  useEffect(() => {
    let cancelled = false
    const statuses = DETAIL_FILTERS.find((f) => f.key === filter)?.statuses ?? []
    setLoading(true)
    setError(null)
    getTestTrackerRun(run.id, statuses)
      .then((res) => {
        if (!cancelled) setDetail(res)
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [run.id, filter])

  // The run row's `failed` collapses failures and errors; the detail counts
  // keep them apart, so show the split whenever we've fetched the red rows.
  const split =
    filter === 'failures' && detail
      ? { failed: detail.counts.failed ?? 0, error: detail.counts.error ?? 0 }
      : null

  // Space-separated terms AND together, matched against the whole id — so
  // "place parse" finds live parsing specs about place tagging.
  const terms = useMemo(
    () => query.toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  )
  const visibleTests = useMemo(() => {
    const tests = detail?.tests ?? []
    if (terms.length === 0) return tests
    return tests.filter((t) => {
      const hay = `${t.test_id} ${t.name ?? ''} ${t.classname ?? ''}`.toLowerCase()
      return terms.every((term) => hay.includes(term))
    })
  }, [detail, terms])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-gray-600">
        <span className="font-mono">
          run #{run.id} · {run.suite} · {shortSha(run.git_sha)} · env {run.env ?? '—'} · clone{' '}
          {run.clone ?? '—'} · exit {run.exit_code ?? '—'}
        </span>
        {split && (
          <span>
            <span className="text-rose-700 font-medium">{split.failed} failed</span>
            {' · '}
            <span className="text-amber-700 font-medium">{split.error} error</span>
          </span>
        )}
        <div className="flex items-center gap-1 ml-auto">
          {DETAIL_FILTERS.map((f) => (
            <motion.button
              key={f.key}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setFilter(f.key)}
              className={`px-2 py-1 rounded border text-xs ${
                filter === f.key
                  ? 'bg-action text-plum border-amber-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </motion.button>
          ))}
        </div>
      </div>

      {run.error_message && (
        <div className="mb-3">
          <div className="text-[10px] font-medium text-amber-700 uppercase tracking-wide">
            Harness error — the suite never produced a report
          </div>
          <FailureMessage message={run.error_message} />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter tests — e.g. place parse"
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded border border-gray-300 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-500"
        />
        {query && (
          <>
            <span className="shrink-0 text-xs text-gray-500 tabular-nums">
              {visibleTests.length} of {detail?.tests.length ?? 0}
            </span>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setQuery('')}
              className="shrink-0 text-gray-400 hover:text-gray-600 text-sm leading-none"
              aria-label="Clear filter"
            >
              ×
            </motion.button>
          </>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {(['tree', 'list'] as const).map((v) => (
            <motion.button
              key={v}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setView(v)}
              className={`px-2 py-1 rounded border text-xs ${
                view === v
                  ? 'bg-action text-plum border-amber-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {v === 'tree' ? 'Tree' : 'List'}
            </motion.button>
          ))}
        </div>
      </div>

      {error && <div className="text-xs text-rose-700">{error}</div>}
      {loading && <div className="text-xs text-gray-400">Loading…</div>}
      {detail && !loading && (
        <div className="rounded border border-gray-200 bg-white max-h-[75vh] overflow-y-auto">
          {visibleTests.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500 text-center">
              {detail.tests.length === 0 ? 'No tests with this status.' : 'No tests match this filter.'}
            </div>
          ) : view === 'tree' ? (
            <TestTreeView tests={visibleTests} filtered={terms.length > 0} />
          ) : (
            visibleTests.map((t) => <TestRow key={t.id} test={t} />)
          )}
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------ launch button

// The suites run on the EC2 integration box, not here, so launching one means
// dispatching `.github/workflows/test-tracker.yml`. Only the live pair is
// offered: the cheap three already run on every push, while the live pair was
// scaled back to once a day and this is the way to ask for another.
//
// The click is deliberately two-step. A live run is ~45 minutes of real,
// billed model calls, which is exactly the cost this button exists to keep
// discretionary — an accidental one would undo the point of the daily gate.
export const LaunchLiveRun: React.FC<{
  lastLive: TestSuiteRun | null
  onLaunched: () => void
}> = ({ lastLive, onLaunched }) => {
  const [status, setStatus] = useState<TestDispatchStatus | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // Set by a 409: a run is already going and the next click has to mean it.
  const [conflict, setConflict] = useState(false)
  const [poll, setPoll] = useState(0)
  const repoll = React.useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(repoll.current), [])

  useEffect(() => {
    let cancelled = false
    getTestTrackerDispatchStatus()
      .then((s) => !cancelled && setStatus(s))
      // A backend too old for the route, or one that 404s the whole surface:
      // the page's own error banner already covers that case.
      .catch(() => !cancelled && setStatus(null))
    return () => {
      cancelled = true
    }
  }, [poll])

  const launch = async (force: boolean) => {
    setBusy(true)
    setProblem(null)
    try {
      const res = await dispatchTestTrackerRun('live', force)
      setResult(res.workflow_url)
      setConfirming(false)
      setConflict(false)
      // GitHub needs a moment to materialize the run before it shows up as
      // in-flight, so re-read rather than claiming it started.
      repoll.current = setTimeout(() => setPoll((p) => p + 1), 5000)
      onLaunched()
    } catch (e: any) {
      setProblem(apiMessage(e, 'Could not launch a run'))
      setConflict(e?.response?.status === 409)
    } finally {
      setBusy(false)
    }
  }

  const inFlight = status?.in_flight?.[0]
  const disabled = busy || !status?.configured

  return (
    <div className="flex flex-col items-start sm:items-end gap-1 min-w-0 sm:shrink-0">
      <div className="flex items-center gap-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {confirming ? (
            <motion.div key="confirm" {...SURFACE_POP} className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                transition={ICON_SPRING}
                disabled={busy}
                onClick={() => launch(conflict)}
                className="px-3 py-1.5 rounded-md text-sm whitespace-nowrap border bg-action text-plum border-amber-600 disabled:opacity-50"
              >
                {busy ? 'Launching…' : conflict ? 'Launch anyway' : 'Yes, run them'}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                transition={ICON_SPRING}
                onClick={() => {
                  setConfirming(false)
                  setProblem(null)
                  setConflict(false)
                }}
                className="w-7 h-7 rounded-md border bg-white text-gray-500 border-gray-300 hover:bg-gray-100 leading-none"
                aria-label="Cancel"
              >
                ×
              </motion.button>
            </motion.div>
          ) : (
            <motion.button
              key="launch"
              {...SURFACE_POP}
              whileHover={disabled ? undefined : { scale: 1.1 }}
              whileTap={disabled ? undefined : { scale: 0.9 }}
              disabled={disabled}
              onClick={() => {
                setResult(null)
                setConfirming(true)
              }}
              title={
                status?.configured
                  ? 'Dispatches the live-LLM suites on the integration box'
                  : 'This backend has no GITHUB_DISPATCH_TOKEN, so it cannot launch a run'
              }
              className="px-3 py-1.5 rounded-md text-sm whitespace-nowrap bg-secondary text-gray-700 hover:bg-secondary-dark disabled:opacity-50 disabled:hover:bg-secondary"
            >
              Run live suites
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <div className="text-xs text-gray-500 sm:text-right max-w-xs">
        {confirming && !conflict && (
          <span className="text-amber-800">
            ~45 min of real, billed model calls.
          </span>
        )}
        {!confirming && inFlight && (
          <a
            href={inFlight.html_url}
            target="_blank"
            onClick={interceptExternalLinkClick}
            rel="noreferrer"
            className="text-amber-800 hover:underline"
          >
            A run is {inFlight.status.replace('_', ' ')} ({inFlight.event}) →
          </a>
        )}
        {!confirming && !inFlight && (
          <span>last live run {formatAgo(lastLive?.started_at ?? null)}</span>
        )}
      </div>

      {result && !problem && (
        <a
          href={result}
          target="_blank"
          onClick={interceptExternalLinkClick}
          rel="noreferrer"
          className="text-xs text-green-700 hover:underline"
        >
          Dispatched — watch it in Actions →
        </a>
      )}
      {problem && <div className="text-xs text-rose-700 sm:text-right max-w-xs">{problem}</div>}
      {status && !status.configured && !problem && (
        <div className="text-xs text-gray-400 sm:text-right max-w-xs">
          No GITHUB_DISPATCH_TOKEN here —{' '}
          <a
            href={status.workflow_url}
            target="_blank"
            onClick={interceptExternalLinkClick}
            rel="noreferrer"
            className="hover:underline"
          >
            run it from Actions
          </a>
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------------------- page

const TestTrackerPage: React.FC = () => {
  const [latest, setLatest] = useState<{ suite: string; run: TestSuiteRun | null }[]>([])
  const [suites, setSuites] = useState<string[]>([])
  const [runs, setRuns] = useState<TestSuiteRun[]>([])
  const [flaky, setFlaky] = useState<FlakyTest[]>([])
  const [flakyWindow, setFlakyWindow] = useState(10)
  const [windowRuns, setWindowRuns] = useState(0)
  const [suite, setSuite] = useState<string | null>(null)
  const [env, setEnv] = useState<string | null>(null)
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([getTestTrackerSuites(), getTestTrackerRuns({ limit: 200 })])
      .then(([suiteRes, runRes]) => {
        if (cancelled) return
        setLatest(suiteRes.suites)
        setSuites(runRes.suites)
        setRuns(runRes.runs)
      })
      .catch((e) => {
        if (cancelled) return
        const status = e?.response?.status
        setError(
          status === 404
            ? 'The tracker API is not enabled on this backend — relaunch it with DEV_TOOLS_ENABLED=1.'
            : status === 403
              ? 'This account is not the admin user, so the tracker API refuses it.'
              : e?.response?.data?.detail || e.message || 'Failed to load',
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  useEffect(() => {
    let cancelled = false
    getTestTrackerFlaky({ suite, runs: flakyWindow })
      .then((res) => {
        if (cancelled) return
        setFlaky(res.flaky)
        setWindowRuns(res.window_runs)
      })
      .catch(() => {
        if (!cancelled) setFlaky([])
      })
    return () => {
      cancelled = true
    }
  }, [suite, flakyWindow, reloadKey])

  // Only worth a control once more than one env has reported — ad-hoc runs are
  // `local`, launchd's schedule is `local-scheduled`, the EC2 box `integration`.
  const envs = useMemo(
    () => Array.from(new Set(runs.map((r) => r.env).filter((e): e is string => !!e))),
    [runs],
  )

  const filteredRuns = useMemo(
    () =>
      runs.filter((r) => (!suite || r.suite === suite) && (!env || r.env === env)),
    [runs, suite, env],
  )

  // Charts read oldest → newest; the API returns newest first.
  const chronological = useMemo(() => [...filteredRuns].reverse(), [filteredRuns])

  // The launch button's "last live run" line. `e2e-live` and not `backend-live`
  // because it is the expensive half, and the one the daily gate is really
  // about. Unfiltered by env on purpose: it answers "when did this last run
  // anywhere", which is the question about spend.
  const liveRun = useMemo(
    () => latest.find((l) => l.suite === 'e2e-live')?.run ?? null,
    [latest],
  )

  const suitesInView = useMemo(
    () => suites.filter((s) => chronological.some((r) => r.suite === s)),
    [suites, chronological],
  )

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <Link to="/admin" className="text-sm text-amber-800 hover:underline">
              ← Admin
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mt-1">Test Tracker</h1>
            <p className="text-sm text-gray-500 mt-1">
              Suite-run history from <span className="font-mono">memgenie_dev</span>. Runs come
              from <span className="font-mono">tools/test_tracker/runner.py</span> — every push to{' '}
              <span className="font-mono">main</span>, with the live-LLM suites at most once a day.
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-2 min-w-0 sm:shrink-0">
            <LaunchLiveRun
              lastLive={liveRun}
              onLaunched={() => setReloadKey((k) => k + 1)}
            />
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setReloadKey((k) => k + 1)}
              className="px-3 py-1.5 rounded-md text-sm whitespace-nowrap bg-secondary text-gray-700 hover:bg-secondary-dark"
            >
              Refresh
            </motion.button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          {latest.map(({ suite: name, run }) => (
            <SuiteCard
              key={name}
              suite={name}
              run={run}
              selected={suite === name}
              onSelect={() => setSuite(suite === name ? null : name)}
            />
          ))}
          {latest.length === 0 && !loading && !error && (
            <div className="text-sm text-gray-500">No runs recorded yet.</div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={ICON_SPRING}
            onClick={() => setSuite(null)}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              suite === null
                ? 'bg-action text-plum border-amber-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
          >
            All suites
          </motion.button>
          {suites.map((s) => (
            <motion.button
              key={s}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              onClick={() => setSuite(s)}
              className={`px-3 py-1.5 rounded-md text-sm border font-mono ${
                suite === s
                  ? 'bg-action text-plum border-amber-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {s}
            </motion.button>
          ))}
          {envs.length > 1 && (
            <select
              value={env ?? ''}
              onChange={(e) => setEnv(e.target.value || null)}
              className="ml-2 px-2 py-1.5 rounded-md text-sm border border-gray-300 bg-white text-gray-700"
            >
              <option value="">All envs</option>
              {envs.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">
            {suite ? `Trend — ${suite}` : 'Trend — pass rate by suite'}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {suite
              ? 'Absolute counts per run, oldest to newest.'
              : 'Pass rate is the only metric comparable across suites — a 640-test backend run and a 39-test e2e run share no absolute scale. Runs with a harness error have no pass rate and leave a gap.'}
          </p>
          <div className="h-64">
            {chronological.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                No runs match this filter.
              </div>
            ) : suite ? (
              <PassFailBars runs={chronological} />
            ) : (
              <PassRateLines runs={chronological} suites={suitesInView} />
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">
            Duration
            {suite && (
              <span className="ml-2 text-base font-normal text-gray-400">
                — {suite} ({durationScale(chronological).unit})
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Each suite gets its own axis and unit — backend runs take ~20 minutes and frontend
            ~10 seconds, so one shared scale would flatten everything but the backend.
          </p>
          {suite ? (
            <div className="h-64">
              {chronological.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  No runs match this filter.
                </div>
              ) : (
                <DurationChart runs={chronological} />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {suitesInView.map((s) => {
                const suiteRuns = chronological.filter((r) => r.suite === s)
                return (
                  <div key={s} className="border border-gray-200 rounded-lg p-3">
                    <div className="font-mono text-xs font-semibold text-gray-700 mb-1">
                      {s}{' '}
                      <span className="font-sans font-normal text-gray-400">
                        ({durationScale(suiteRuns).unit})
                      </span>
                    </div>
                    <div className="h-40">
                      <DurationChart runs={suiteRuns} compact />
                    </div>
                  </div>
                )
              })}
              {suitesInView.length === 0 && (
                <div className="text-sm text-gray-500">No runs match this filter.</div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Flaky tests</h2>
              <p className="text-sm text-gray-500">
                Tests that both passed and failed in the window (<span className="font-mono">mixed</span>
                ), or that Playwright retried into a pass (
                <span className="font-mono">retry</span> — stored as passed, so status alone
                hides them). Window covers {windowRuns} run{windowRuns === 1 ? '' : 's'}.
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {FLAKY_WINDOWS.map((n) => (
                <motion.button
                  key={n}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  transition={ICON_SPRING}
                  onClick={() => setFlakyWindow(n)}
                  className={`px-2.5 py-1 rounded-md text-xs border ${
                    flakyWindow === n
                      ? 'bg-action text-plum border-amber-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  last {n}
                </motion.button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-3 py-2 font-medium">Suite</th>
                  <th className="px-3 py-2 font-medium">Test</th>
                  <th className="px-3 py-2 font-medium text-right">Runs</th>
                  <th className="px-3 py-2 font-medium text-right">Pass</th>
                  <th className="px-3 py-2 font-medium text-right">Fail</th>
                  <th className="px-3 py-2 font-medium text-right">Retry</th>
                  <th className="px-3 py-2 font-medium text-right">Fail rate</th>
                  <th className="px-3 py-2 font-medium">Why</th>
                </tr>
              </thead>
              <tbody>
                {flaky.map((f) => (
                  <FlakyRow key={`${f.suite}:${f.test_id}`} flaky={f} />
                ))}
                {flaky.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-6 text-center text-gray-500">
                      No flaky tests in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Runs</h2>
            <p className="text-sm text-gray-500">
              Newest first by start time. Click a run for its per-test detail.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-3 py-2 font-medium">Run</th>
                  <th className="px-3 py-2 font-medium">Suite</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Started</th>
                  <th className="px-3 py-2 font-medium text-right">Duration</th>
                  <th className="px-3 py-2 font-medium text-right">Passed</th>
                  <th className="px-3 py-2 font-medium text-right">Failed</th>
                  <th className="px-3 py-2 font-medium text-right">Skipped</th>
                  <th className="px-3 py-2 font-medium">Commit</th>
                  <th className="px-3 py-2 font-medium">Env</th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((run) => {
                  const open = expandedRunId === run.id
                  return (
                    <React.Fragment key={run.id}>
                      <tr
                        onClick={() => setExpandedRunId(open ? null : run.id)}
                        className={`border-t border-gray-100 cursor-pointer ${
                          open
                            ? 'bg-amber-50'
                            : run.status === 'error'
                              ? 'bg-amber-50 hover:bg-amber-100'
                              : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-2 text-center">
                          <Chevron open={open} />
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-500">#{run.id}</td>
                        <td className="px-3 py-2 font-mono text-gray-800">{run.suite}</td>
                        <td className="px-3 py-2">
                          <StatusBadge
                            status={run.status}
                            title={run.error_message ?? undefined}
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {formatTime(run.started_at)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600 whitespace-nowrap">
                          {formatDuration(run.duration_ms)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-green-700">
                          {run.passed}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums ${
                            run.failed > 0 ? 'text-rose-700 font-medium' : 'text-gray-400'
                          }`}
                        >
                          {run.failed}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                          {run.skipped}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">
                          {shortSha(run.git_sha)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {run.env ?? '—'}/{run.clone ?? '—'}
                        </td>
                      </tr>
                      {open && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td></td>
                          <td colSpan={10} className="px-4 py-4">
                            <motion.div {...SURFACE_POP}>
                              <RunDetail run={run} />
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {filteredRuns.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-6 py-6 text-center text-gray-500">
                      {loading ? 'Loading…' : 'No runs match this filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TestTrackerPage
